/**
 * "Create task from message" dialog — replaces the old fire-and-forget menu
 * item. Title is prefilled from the message text; due date, priority and an
 * assignee are optional. POST /api/messaging/messages/{id}/create-task works
 * without a member and accepts the optional title/description.
 */

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { CreateTaskFromMessageRequest, Message } from '@/services/messagingApi';
import { OrganizationUser } from '@/services/api/userManagement';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

/** Strip mention markup / markdown link syntax and cap at ~80 chars on a word. */
export function taskTitleFromMessage(content: string | null | undefined, max = 80): string {
  const plain = (content || '')
    .replace(/@\[([^\]]+)\]\(\w+:\d+\)/g, '@$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  const cut = plain.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/**
 * Open a task in the app's task editor. app.tsx listens for `v2-quick-action`
 * with action 'edit-task' (the same event My Day's "Open" buttons use) and
 * opens V2QuickTaskModal on that task id.
 */
export function openTaskFromMessaging(taskId: number) {
  if (!taskId) return;
  window.dispatchEvent(new CustomEvent('v2-quick-action', { detail: { action: 'edit-task', taskId } }));
}

type Priority = NonNullable<CreateTaskFromMessageRequest['priority']>;

interface CreateTaskDialogProps {
  message: Message | null;
  onOpenChange: (open: boolean) => void;
  users: OrganizationUser[];
  /** Member the conversation is about, if known (member DMs). */
  memberId?: number | null;
  isPending: boolean;
  /** Server error from the last attempt, shown inside the dialog. */
  error?: string | null;
  onSubmit: (request: CreateTaskFromMessageRequest) => void;
}

export function CreateTaskDialog({
  message,
  onOpenChange,
  users,
  memberId,
  isPending,
  error,
  onSubmit,
}: CreateTaskDialogProps) {
  const { t } = useAppTranslation();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [assignee, setAssignee] = useState('');

  useEffect(() => {
    if (message) {
      setTitle(taskTitleFromMessage(message.content));
      setDueDate(message.action_due_date ? String(message.action_due_date).slice(0, 10) : '');
      setPriority(message.priority === 'urgent' ? 'urgent' : message.priority === 'high' ? 'high' : 'medium');
      setAssignee('');
    }
  }, [message]);

  const submit = () => {
    if (!message || !title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: message.content || undefined,
      priority,
      task_type: 'follow_up',
      due_date: dueDate || undefined,
      assigned_to_user_id: assignee ? Number(assignee) : undefined,
      ...(memberId ? { assigned_to_member_id: memberId } : {}),
    });
  };

  const selectClass =
    'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <Dialog open={!!message} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('messaging.task.dialogTitle', 'Create task from message')}</DialogTitle>
          <DialogDescription>
            {t('messaging.task.dialogDescription', 'The task is linked back to this message.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-sm font-medium">{t('messaging.task.title', 'Title')}</span>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-sm font-medium">{t('messaging.task.dueDate', 'Due date')}</span>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">{t('messaging.task.priority', 'Priority')}</span>
              <select
                className={selectClass}
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
              >
                <option value="low">{t('messaging.task.priorityLow', 'Low')}</option>
                <option value="medium">{t('messaging.task.priorityMedium', 'Medium')}</option>
                <option value="high">{t('messaging.task.priorityHigh', 'High')}</option>
                <option value="urgent">{t('messaging.task.priorityUrgent', 'Urgent')}</option>
              </select>
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">
              {t('messaging.task.assignee', 'Assignee (optional)')}
            </span>
            <select className={selectClass} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">{t('messaging.task.unassigned', 'Unassigned')}</option>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          >
            <div className="font-medium">{t('messaging.task.createFailed', 'Could not create task')}</div>
            <div className="text-xs">{error}</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('messaging.channel.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={isPending || !title.trim()}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('messaging.task.create', 'Create task')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateTaskDialog;
